import { useEffect, useRef } from 'react'
import { Link } from 'react-router-dom'
import { ArrowLeft, ArrowRight } from 'lucide-react'
import gsap from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'

gsap.registerPlugin(ScrollTrigger)

const thumbnails = [
  '/images/technoarreda.png',
  '/images/cosematics.png',
  '/images/redsun.png',
  '/images/voxelo.png',
  '/images/loud.png',
  '/images/music.png',
  '/images/stokt.png',
  '/images/buck.png',
]

export default function MoreProjects() {
  const sectionRef = useRef<HTMLDivElement>(null)
  const trackRef = useRef<HTMLDivElement>(null)
  const carouselRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const ctx = gsap.context(() => {
      gsap.fromTo(
        sectionRef.current,
        { y: 40, opacity: 0 },
        {
          y: 0,
          opacity: 1,
          duration: 0.8,
          ease: 'power3.out',
          scrollTrigger: {
            trigger: sectionRef.current,
            start: 'top 85%',
            toggleActions: 'play none none none',
          },
        }
      )

      gsap.fromTo(
        '.thumbnail-item',
        { y: 30, opacity: 0, scale: 0.9 },
        {
          y: 0,
          opacity: 1,
          scale: 1,
          duration: 0.6,
          ease: 'power2.out',
          stagger: 0.1,
          scrollTrigger: {
            trigger: sectionRef.current,
            start: 'top 80%',
            toggleActions: 'play none none none',
          },
        }
      )

      const handleMouseEnter = (e: MouseEvent) => {
        const thumb = e.currentTarget as HTMLElement
        gsap.to(thumb, {
          scale: 1.05,
          duration: 0.3,
          ease: 'power2.out',
        })
      }

      const handleMouseLeave = (e: MouseEvent) => {
        const thumb = e.currentTarget as HTMLElement
        gsap.to(thumb, {
          scale: 1,
          duration: 0.3,
          ease: 'power2.out',
        })
      }

      const thumbElements = sectionRef.current?.querySelectorAll('.thumbnail-item')
      thumbElements?.forEach((thumb) => {
        thumb.addEventListener('mouseenter', handleMouseEnter)
        thumb.addEventListener('mouseleave', handleMouseLeave)
      })

      const track = trackRef.current
      const carousel = carouselRef.current
      if (track && carousel) {
        const totalWidth = track.scrollWidth / 2

        const anim = gsap.to(track, {
          x: -totalWidth,
          ease: 'none',
          duration: 40,
          repeat: -1,
        })

        carousel.addEventListener('mouseenter', () => anim.pause())
        carousel.addEventListener('mouseleave', () => anim.play())

        const handlePrev = () => {
          gsap.to(track, {
            x: `+=${totalWidth * 0.3}`,
            duration: 0.6,
            ease: 'power2.out',
            onComplete: () => {
              const currentX = gsap.getProperty(track, 'x') as number
              if (currentX > 0) {
                gsap.set(track, { x: currentX - totalWidth })
              }
            },
          })
        }

        const handleNext = () => {
          gsap.to(track, {
            x: `-=${totalWidth * 0.3}`,
            duration: 0.6,
            ease: 'power2.out',
            onComplete: () => {
              const currentX = gsap.getProperty(track, 'x') as number
              if (currentX < -totalWidth) {
                gsap.set(track, { x: currentX + totalWidth })
              }
            },
          })
        }

        const win = window as Window & {
          __carouselPrev?: () => void
          __carouselNext?: () => void
        }

        win.__carouselPrev = handlePrev
        win.__carouselNext = handleNext
      }
    }, sectionRef)

    return () => ctx.revert()
  }, [])

  return (
    <section ref={sectionRef} className="bg-[#050505] py-12 px-6 lg:px-10">
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6 mb-8">
        <div className="text-6xl lg:text-8xl font-light text-white">(124)</div>
        <Link
          to="/portfolio"
          className="flex items-center gap-3 text-xl lg:text-2xl text-white hover:text-[#9A9A9A] transition-colors group"
        >
          MORE PROJECTS
          <ArrowRight className="w-6 h-6 transition-transform group-hover:-translate-x-1" />
        </Link>
      </div>

      {/* Carousel */}
      <div ref={carouselRef} className="relative">
        <div className="overflow-hidden">
          <div ref={trackRef} className="flex gap-3 will-change-transform">
            {[...thumbnails, ...thumbnails].map((src, i) => (
              <div
                key={i}
                className="thumbnail-item flex-shrink-0 w-24 h-16 lg:w-32 lg:h-20 rounded-xl overflow-hidden"
              >
                <img src={src} alt="" className="w-full h-full object-cover" />
              </div>
            ))}
          </div>
        </div>

        {/* Navigation buttons */}
        <button
          onClick={() => {
            const win = window as Window & {
              __carouselPrev?: () => void
            }
            win.__carouselPrev?.()
          }}
          className="absolute left-2 top-1/2 -translate-y-1/2 z-10 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white transition-colors"
          aria-label="Previous"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
        <button
          onClick={() => {
            const win = window as Window & {
              __carouselNext?: () => void
            }
            win.__carouselNext?.()
          }}
          className="absolute right-2 top-1/2 -translate-y-1/2 z-10 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white transition-colors"
          aria-label="Next"
        >
          <ArrowRight className="w-5 h-5" />
        </button>
      </div>
    </section>
  )
}
